import { infiniteQueryOptions } from '@tanstack/react-query';
import { BFF_API_URL } from '@frodo/lib/services/http/shared/constants';
import ApiHttpClientService from '@frodo/lib/services/http/base/api-http-client.service';
import {
  MUTATION_KEYS,
  QUERY_KEYS,
} from '@frodo/lib/services/http/shared/queryKeys';
import { buildReviewHubQueryParams } from '@frodo/lib/utils/common/reviewHubUtils';
import { REVIEWS_PAGE_SIZE } from '@frodo/lib/utils/common/constants';
import { mutationOptions } from '@frodo/lib/utils/client';
import type {
  ReviewFilterApplyParams,
  ReviewFilterParams,
  ReviewSubmissionPayload,
  ReviewSubmissionResponse,
  ReviewsResponse,
} from '@frodo/models/types';

class ApiReviewsClientService extends ApiHttpClientService {
  public async fetchReviews(
    productSku: string,
    store: string,
    params: ReviewFilterParams,
  ): Promise<ReviewsResponse> {
    return this.get<ReviewsResponse>(BFF_API_URL.reviews(productSku, store), {
      params: buildReviewHubQueryParams(params),
    });
  }

  public async submitReview(
    sku: string,
    payload: ReviewSubmissionPayload,
  ): Promise<ReviewSubmissionResponse> {
    return this.post<ReviewSubmissionPayload, ReviewSubmissionResponse>(
      BFF_API_URL.submitReview(sku),
      payload,
    );
  }
}

export const apiReviewsClientService = new ApiReviewsClientService();

export const reviewsInfiniteQuery = (
  productSku: string,
  store: string,
  filters: ReviewFilterApplyParams,
  perPage: number = REVIEWS_PAGE_SIZE,
) => {
  return infiniteQueryOptions({
    queryKey: QUERY_KEYS.productReviews(productSku, store, filters, perPage),
    queryFn: ({ pageParam }) =>
      apiReviewsClientService.fetchReviews(productSku, store, {
        ...filters,
        page: pageParam,
        limit: perPage,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => {
      const { page, totalPages } = lastPage.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
  });
};

export const submitReviewMutation = (sku: string) => {
  return mutationOptions({
    mutationKey: MUTATION_KEYS.submitReview(sku),
    mutationFn: (payload: ReviewSubmissionPayload) =>
      apiReviewsClientService.submitReview(sku, payload),
  });
};
